import { FC, useEffect, useState } from "react"

import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { scrollbarStyles } from "themes/scrollbar"
import { IGroupScope } from "ts/interfaces/Group"
import { groupScopeChipSx } from "utils/groupScope"

export interface IGroupFilterOption {
  id: number
  name: string
  scope: IGroupScope
}

interface IProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  groups: IGroupFilterOption[]
  // `null` = no preference saved — falls back to `defaultAllChecked`.
  savedSelection: number[] | null
  onApply: (ids: number[]) => void
  onReset: () => void
  title: string
  helperText: string
  defaultAllChecked?: boolean
}

const GroupFilterMenu: FC<IProps> = ({
  anchorEl,
  open,
  onClose,
  groups,
  savedSelection,
  onApply,
  onReset,
  title,
  helperText,
  defaultAllChecked = true,
}) => {
  const { t } = useTranslation()
  const allIds = groups.map((group) => group.id)
  const defaultSelection = defaultAllChecked ? allIds : []
  const [selectedIds, setSelectedIds] = useState<number[]>(
    savedSelection ?? defaultSelection,
  )

  useEffect(() => {
    if (open) setSelectedIds(savedSelection ?? defaultSelection)
    // Only reset local state when the popover opens — not on every savedSelection/groups
    // change, which would otherwise wipe unsaved edits while it's open.
  }, [open]) // eslint-disable-line

  const toggle = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id],
    )

  const toggleSection = (sectionIds: number[], allSelected: boolean) =>
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !sectionIds.includes(id))
        : [...new Set([...prev, ...sectionIds])],
    )

  const classGroups = groups.filter((group) => group.scope !== "SCHOOL")
  const schoolGroups = groups.filter((group) => group.scope === "SCHOOL")

  const classIds = classGroups.map((group) => group.id)
  const allClassSelected =
    classIds.length > 0 && classIds.every((id) => selectedIds.includes(id))
  const someClassSelected = classIds.some((id) => selectedIds.includes(id))

  const schoolIds = schoolGroups.map((group) => group.id)
  const allSchoolSelected =
    schoolIds.length > 0 && schoolIds.every((id) => selectedIds.includes(id))
  const someSchoolSelected = schoolIds.some((id) => selectedIds.includes(id))

  const sectionChipSx = {
    class: {
      ...groupScopeChipSx.CLASS,
      fontWeight: 700,
      height: "28px",
      "& .MuiChip-label": { fontSize: "15px", px: "12px" },
    },
    school: {
      ...groupScopeChipSx.SCHOOL,
      fontWeight: 700,
      height: "28px",
      "& .MuiChip-label": { fontSize: "15px", px: "12px" },
    },
  }

  const renderSelectAll = (
    checked: boolean,
    indeterminate: boolean,
    onChange: () => void,
  ) => (
    <FormControlLabel
      sx={{
        ml: "-8px",
        mr: 0,
        pb: "8px",
        borderBottom: (muiTheme) =>
          `1px solid ${muiTheme.palette.mockup.neutral90}`,
      }}
      control={
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={onChange}
          sx={{
            width: "20px",
            height: "20px",
            "& .MuiSvgIcon-root": { fontSize: "20px" },
          }}
        />
      }
      label={
        <Typography variant="body2" fontStyle="italic" color="mockup.neutral40">
          {t("group.selectAll")}
        </Typography>
      }
    />
  )

  const renderGroupCheckbox = (group: IGroupFilterOption) => (
    <FormControlLabel
      key={group.id}
      sx={{ ml: "-8px", mr: 0 }}
      control={
        <Checkbox
          checked={selectedIds.includes(group.id)}
          onChange={() => toggle(group.id)}
          sx={{
            width: "20px",
            height: "20px",
            "& .MuiSvgIcon-root": { fontSize: "20px" },
          }}
        />
      }
      label={
        <Typography variant="body1" fontWeight={500}>
          {group.name}
        </Typography>
      }
    />
  )

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Stack gap="16px" p="32px" minWidth="260px" maxWidth="320px">
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="mockup.neutral40">
          {helperText}
        </Typography>
        <Stack
          maxHeight="328px"
          overflow="auto"
          gap="16px"
          sx={scrollbarStyles}
        >
          {classGroups.length > 0 && (
            <Stack gap="12px">
              <Chip
                label={t("group.filterSectionClass")}
                size="small"
                sx={sectionChipSx.class}
              />
              {renderSelectAll(allClassSelected, someClassSelected && !allClassSelected, () =>
                toggleSection(classIds, allClassSelected),
              )}
              {classGroups.map(renderGroupCheckbox)}
            </Stack>
          )}
          {schoolGroups.length > 0 && (
            <Stack gap="12px">
              <Chip
                label={t("group.filterSectionSchool")}
                size="small"
                sx={sectionChipSx.school}
              />
              {renderSelectAll(
                allSchoolSelected,
                someSchoolSelected && !allSchoolSelected,
                () => toggleSection(schoolIds, allSchoolSelected),
              )}
              {schoolGroups.map(renderGroupCheckbox)}
            </Stack>
          )}
        </Stack>
        <Stack mt="8px" gap="8px">
          <Button variant="text" onClick={onReset}>
            {t("group.resetFilters")}
          </Button>
          <Button variant="contained" onClick={() => onApply(selectedIds)}>
            {t("group.apply")}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  )
}

export default GroupFilterMenu
